import { useCallback } from 'react';

import {
  DEFAULT_DOCUMENT_NAME,
  isDocumentProject,
  isLongFormProject,
  PROJECT_STATUS,
} from 'src/data/project';
import { useGoBack } from 'src/lib/hooks/useGoBack';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { useCurrentProject, useProjectActions } from 'src/store';

import { PageStore, usePageActions } from '../store';

import { useDeleteProject } from './useDeleteProject';
import { useUpdateProject } from './useUpdateProject';

const usePageState = createStateSelectorHook(PageStore, [
  'projectId',
  'projectType',
  'projectName',
  'generatingMultiPrompt',
]);

export const useOnProjectPageBackClick = () => {
  const projectActions = useProjectActions();
  const currentProject = useCurrentProject();
  const pageActions = usePageActions();
  const projectStatus = currentProject?.status;
  const { projectType, projectName, generatingMultiPrompt } = usePageState();
  const deleteProject = useDeleteProject();
  const goBack = useGoBack();

  const renameProject = useUpdateProject({
    isDisplayedBeforeGoBack: true,
    onConfirm: () => {
      goBack();
    },
    onCancel: async () => {
      await deleteProject();
      goBack();
    },
  });

  const handleDocumentGoBack = useCallback(() => {
    const isDefaultDocumentName = DEFAULT_DOCUMENT_NAME === projectName;
    const isNewProject = pageActions.isNewProject();

    if (isNewProject && isDefaultDocumentName) {
      if (projectActions.isCurrentProjectEmpty() && !generatingMultiPrompt) {
        deleteProject().then(() => {
          goBack();
        });
      } else {
        renameProject();
      }

      return false;
    }

    return true;
  }, [
    deleteProject,
    goBack,
    projectActions,
    renameProject,
    projectName,
    pageActions,
    generatingMultiPrompt,
  ]);

  const handleBlogGoBack = useCallback(() => {
    const isNewProject = pageActions.isNewProject();

    if (isNewProject && projectStatus === PROJECT_STATUS.longForm) {
      if (projectActions.isCurrentProjectEmpty()) {
        deleteProject().then(() => {
          goBack();
        });
      } else {
        renameProject();
      }
    } else {
      goBack();
    }
  }, [goBack, projectActions, renameProject, deleteProject, projectStatus, pageActions]);

  return useCallback(() => {
    if (isDocumentProject(projectType)) {
      return handleDocumentGoBack();
    } else if (isLongFormProject(projectType)) {
      handleBlogGoBack();
      return false;
    }

    return true;
  }, [projectType, handleDocumentGoBack, handleBlogGoBack]);
};
